import React, { useState, useEffect } from "react";
import {
  BadgeIndianRupee,
  CalendarDays,
  CheckCircle2,
  FileText,
  Hash,
  ShieldCheck,
  Wallet,
  User,
} from "lucide-react";
import { useParams } from "react-router-dom";
import config from "../../../config";

const PaymentDetails = () => {
  const { id } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${config.baseURL}/getdetails/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (!data || data.length === 0) {
          setError("No data found");
          return;
        }

        setDetails(data[0]);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(
          "Failed to load data. Please check the network and try again."
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDetails();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100"></div>
        <p className="mt-4 text-lg text-gray-700 dark:text-gray-300">
          Loading data details...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 text-red-600 dark:text-red-400">
        <p className="text-xl">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <p className="text-xl text-gray-700 dark:text-gray-300">
          Data not found. Please check.
        </p>
      </div>
    );
  }

  const formattedDate = new Date(details.created_at).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const StatusBadge = ({ status }) => {
    const isSuccess = status.toLowerCase() === "paid";
    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          isSuccess ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
        }`}
      >
        {isSuccess ? (
          <CheckCircle2 className="w-4 h-4 mr-1" />
        ) : (
          <ShieldCheck className="w-4 h-4 mr-1" />
        )}
        {status}
      </span>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-6 mt-10">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-100">
        <h2 className="text-3xl font-semibold text-gray-800 mb-6 flex items-center gap-3 text-[#1f2937] dark:text-gray-300">
          <Wallet className="w-7 h-7 text-indigo-600 text-[#4f46e5] dark:text-white" />
          Payment Receipt
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700 text-[15px]">
          <div className="flex items-center dark:text-gray-400">
            <User className="w-5 h-5 text-indigo-500 mr-2 text-[#6366f1] dark:text-white"/>
            <strong className="text-[#374151] dark:text-gray-200">Name:</strong>&nbsp; {details.first_name} {details.lastname}
          </div>
          <div className="flex items-center dark:text-gray-400">
            <Hash className="w-5 h-5 text-indigo-500 mr-2 text-[#6366f1] dark:text-white" />
            <strong className="text-[#374151] dark:text-gray-200">Invoice No:</strong>&nbsp; {details.invoice_no}
          </div>
          <div className="flex items-center dark:text-gray-400">
            <Hash className="w-5 h-5 text-indigo-500 mr-2 text-[#6366f1] dark:text-white" />
            <strong className="text-[#374151] dark:text-gray-200">Order ID:</strong>&nbsp; {details.order_id}
          </div>
          <div className="flex items-center dark:text-gray-400">
            <FileText className="w-5 h-5 text-indigo-500 mr-2 text-[#6366f1] dark:text-white" />
            <strong className="text-[#374151] dark:text-gray-200">Payment ID:</strong>&nbsp;
          </div>
          <div className="flex items-center dark:text-gray-400">
            <BadgeIndianRupee className="w-5 h-5 text-indigo-500 mr-2 text-[#6366f1] dark:text-white" />
            <strong className="text-[#374151] dark:text-gray-200">Amount:</strong>&nbsp; ₹{details.amount}
          </div>
          <div className="flex items-center dark:text-gray-400">
            <span className="w-5 h-5 mr-2 text-indigo-500 font-bold text-lg text-[#6366f1] dark:text-white">
              ₹
            </span>
            <strong className="text-[#374151] dark:text-gray-200">Currency:</strong>&nbsp; {details.currency}
          </div>
          <div className="flex items-center dark:text-gray-400">
            <CalendarDays className="w-5 h-5 text-indigo-500 mr-2 text-[#6366f1] dark:text-white" />
            <strong className="text-[#374151] dark:text-gray-200">Created:</strong>&nbsp; {formattedDate}
          </div>
          <div className="flex items-center dark:text-gray-400">
            <CheckCircle2 className="w-5 h-5 text-indigo-500 mr-2 text-[#6366f1] dark:text-white" />
            <strong className="text-[#374151] dark:text-gray-200">Status:</strong>&nbsp;
            <StatusBadge status={details.status} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetails;
